import useCourseTreeNavigate from '@/data/hook/course-line/useCourseTreeNavigate';
import { useTranslation } from 'react-i18next';

import CoursePathEvaluationsTabContent from '@/components/evaluation/CoursePathEvaluationsTabContent';
import ExamsTabContent from '@/components/exam/ExamsTabContent';
import LessonPlansTabContent from '@/components/lesson-plan/LessonPlansTabContent';
import ContPedStatusButton from '@/components/ped-content/ContPedStatusButton';
import CourseFormService, {
  CourseFormServiceSuccess,
} from '@/components/ped-content/CourseFormService';
import { WorkspaceHeader } from '@/components/shared/WorkspaceHeader';
import WorkspaceLayout from '@/components/shared/WorkspaceLayout';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Skeleton, Tabs } from '@/components/ui/daisy';
import UnitsTabContent from '@/components/unit/UnitsTabContent';
import { ORDENABLE_PAGE_SIZE } from '@/constants';
import { useTreeCourses } from '@/data/context/TreeCoursesContext';
import usePedWorkspace from '@/data/hook/ped-content/usePedWorkspace';
import {
  useInfiniteCache,
  useListCache,
} from '@/data/hook/query/v2/useCacheManager';
import useWorkspaceTabs from '@/data/hook/useWorkspaceTabs';
import { coursePathsQueryKeys } from '@/data/services/querykeys';
import CoursePath from '@/models/CoursePath';
import { mountKey } from '@/utils/courses';
import { ROUTES } from '@/utils/routes';
import { CoursesNode } from '@/utils/trees/TreeCourses';
import CoursePathVersionSelect from '@/components/ped-content/CoursePathVersionSelect';

enum CourseTabs {
  INFO = 'info',
  UNITS = 'units',
  EVALUATIONS = 'evaluations',
  EXAMS = 'exams',
  LESSON_PLANS = 'lesson-plans',
}

export default function CourseWorkspace() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.courseWorkspace',
  });
  const { tree } = useTreeCourses();
  const { coursePath, courseLineId, moduleId, slug, isLoading, cache } =
    usePedWorkspace();
  const { setTab, tab } = useWorkspaceTabs<CourseTabs>({
    defaultTab: CourseTabs.INFO,
  });
  const { navigate } = useCourseTreeNavigate();
  const cacheManager = useInfiniteCache(
    coursePathsQueryKeys.list({ moduleId: [moduleId], uniqueCourse: true })._ctx
      .infinity.queryKey,
  );
  const listCacheManager = useListCache(
    coursePathsQueryKeys.list({
      courseLineId: [courseLineId],
      pageSize: ORDENABLE_PAGE_SIZE,
    }).queryKey,
  );
  const handleSuccessReplace = (item: CoursePath) => {
    const oldKey = mountKey({ type: 'course', id: slug! });
    const newKey = mountKey({ type: 'course', id: item.slug });
    const parentKey = mountKey({ type: 'module', id: moduleId });
    const newNode: CoursesNode = {
      id: newKey,
      name: item.course.abbreviation,
      type: 'course',
      realId: item.slug,
    };
    tree?.current.delete(oldKey);
    tree?.current.insert(newNode, parentKey);
    navigate(
      ROUTES.ADMIN.COURSE_LINES.PED_CONTENT(courseLineId, moduleId, item.slug),
      { replace: true, treeKey: newKey },
    );
  };

  const handleSuccessUpdate = (item: CoursePath) => {
    const slugChanged = slug !== item.slug;

    cacheManager.update(item);
    listCacheManager.update(item);

    if (!slugChanged) {
      tree?.current.update({
        id: mountKey({ type: 'course', id: item.slug }),
        name: item.course.abbreviation,
      });
      return;
    }

    handleSuccessReplace(item);
  };

  const handleSuccessCreate = (item: CoursePath) => {
    cacheManager.add(item);
    listCacheManager.add(item);
    const parentKey = mountKey({ type: 'module', id: moduleId });
    tree?.current.insert(
      {
        id: mountKey({ type: 'course', id: item.slug }),
        name: item.course.abbreviation,
        type: 'course',
        realId: item.slug,
      },
      parentKey,
    );
    navigate(
      ROUTES.ADMIN.COURSE_LINES.PED_CONTENT(courseLineId, moduleId, item.slug),
      { treeKey: mountKey({ type: 'course', id: item.slug }) },
    );
  };

  const handleSuccess = (response: CourseFormServiceSuccess) => {
    if (response.action === 'update') handleSuccessUpdate(response.item);
    else handleSuccessCreate(response.item);
  };

  if (isLoading) return <LoadingView />;

  return (
    <WorkspaceLayout>
      <div className="flex justify-between items-center w-full">
        <WorkspaceHeader>
          <WorkspaceHeader.Title>{t('title')}</WorkspaceHeader.Title>
          <WorkspaceHeader.Subtitle
            subtitle={coursePath?.course?.abbreviation || t('newCourse')}
          >
            <ConditionalRenderer condition={!!coursePath}>
              <CoursePathVersionSelect />
            </ConditionalRenderer>
          </WorkspaceHeader.Subtitle>
        </WorkspaceHeader>
        {coursePath && (
          <ContPedStatusButton coursePath={coursePath} cache={cache} />
        )}
      </div>
      <Tabs
        variant="box"
        className="w-fit"
        value={tab}
        onChange={value => setTab(value as CourseTabs)}
      >
        <Tabs.Tab value={CourseTabs.INFO}>{t('tabs.information')}</Tabs.Tab>
        <Tabs.Tab value={CourseTabs.UNITS} disabled={!coursePath}>
          {t('tabs.units')}
        </Tabs.Tab>
        <Tabs.Tab value={CourseTabs.EVALUATIONS} disabled={!coursePath}>
          {t('tabs.evaluations')}
        </Tabs.Tab>
        <Tabs.Tab value={CourseTabs.EXAMS} disabled={!coursePath}>
          {t('tabs.exams')}
        </Tabs.Tab>
        <Tabs.Tab value={CourseTabs.LESSON_PLANS} disabled={!coursePath}>
          {t('tabs.lessonPlans')}
        </Tabs.Tab>
      </Tabs>
      <ConditionalRenderer condition={tab === CourseTabs.INFO}>
        <CourseFormService key={coursePath?.id} onSuccess={handleSuccess} />
      </ConditionalRenderer>
      <ConditionalRenderer condition={tab === CourseTabs.UNITS && !!coursePath}>
        {slug && (
          <UnitsTabContent
            slug={slug}
            courseLineId={courseLineId}
            moduleId={moduleId}
          />
        )}
      </ConditionalRenderer>
      <ConditionalRenderer
        condition={tab === CourseTabs.EVALUATIONS && !!coursePath}
      >
        {coursePath && (
          <CoursePathEvaluationsTabContent
            coursePath={coursePath}
            courseLineId={courseLineId}
          />
        )}
      </ConditionalRenderer>
      <ConditionalRenderer condition={tab === CourseTabs.EXAMS && !!coursePath}>
        {coursePath && <ExamsTabContent coursePath={coursePath} />}
      </ConditionalRenderer>
      <ConditionalRenderer
        condition={tab === CourseTabs.LESSON_PLANS && !!coursePath}
      >
        {coursePath && (
          <LessonPlansTabContent
            coursePath={coursePath}
            courseLineId={courseLineId}
          />
        )}
      </ConditionalRenderer>
    </WorkspaceLayout>
  );
}

const LoadingView = () => {
  return (
    <div className="flex flex-col flex-1 rounded-2xl p-6 gap-6 bg-base-100">
      <div className="flex flex-col gap-5">
        <Skeleton className="h-5 w-20" />
        <Skeleton className="h-8 w-40" />
      </div>
      <Skeleton className="h-12 w-52" />
      <Skeleton className="h-96 w-full" />
      <div className="flex justify-end">
        <Skeleton className="h-12 w-32" />
      </div>
    </div>
  );
};
